import { useState } from "react";

import { LinkIcon, PlusIcon, WrenchIcon } from "@heroicons/react/24/outline";

import { Alert } from "../base/Alert";
import Card from "../shared/Card/Card";
import { Button } from "../base/Button";
import LoadingWheel from "../shared/LoadingWheel";
import { errorResolver } from "../../utils/errorResolver";
import { useClipboardCopy } from "../../hooks/useClipboardCopy";
import { CreateUpdateApplicationModal } from "./CreateUpdateApplicationModal";
import useCustomerApplications from "../../data/CustomerApplication/useCustomerApplications";
import { MissingApplicationSettingsBadge } from "../ApplicationSettings/MissingSettingsBadge";
import { CustomerApplicationSettingsModal } from "../ApplicationSettings/CustomerApplicationSettingsModal";
import { ApplicationSettingsConnectionStringModal } from "../ApplicationSettings/ApplicationSettingsConnectionStringModal";

// Customer applications is also know as Application registrations

interface CustomerApplicationListProps {
  customerId: string;
}

export function CustomerApplicationList({
  customerId,
}: CustomerApplicationListProps) {
  const { CopyToClipboardButton } = useClipboardCopy();
  const { customerApplications } = useCustomerApplications(customerId);

  const [appRegOpen, setAppRegOpen] = useState(false);

  const [appSettingsOpen, setAppSettingsOpen] = useState(false);
  const [appSettingsAppRegId, setAppSettingsAppRegId] = useState<string>();

  const [connStringOpen, setConnStringOpen] = useState(false);
  const [connStringString, setConnStringString] = useState<string>();

  const handleOpenAppSettings = (appRegId: string) => {
    setAppSettingsAppRegId(appRegId);
    setAppSettingsOpen(true);
  };

  const handleOpenConnString = (connString: string) => {
    setConnStringString(connString);
    setConnStringOpen(true);
  };

  return (
    <>
      <CreateUpdateApplicationModal
        open={appRegOpen}
        setOpen={() => setAppRegOpen(false)}
        customerId={customerId}
      />
      <CustomerApplicationSettingsModal
        open={appSettingsOpen}
        setOpen={() => setAppSettingsOpen(false)}
        applicationRegistrationId={appSettingsAppRegId!}
      />
      <ApplicationSettingsConnectionStringModal
        open={connStringOpen}
        setOpen={setConnStringOpen}
        connectionString={connStringString}
      />
      <Card
        title="Applications"
        bodyClassName="px-4 divide-y divide-gray-100"
        titleRightContent={
          <Button
            onClick={() => setAppRegOpen(true)}
            size="sm"
            className="my-[1px] flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5 -ml-1" />
            <p>Add Application</p>
          </Button>
        }
      >
        {customerApplications.isLoading ? (
          <div className="py-4">
            <LoadingWheel />
          </div>
        ) : customerApplications.isError ? (
          <div className="p-4">
            <Alert
              severity="error"
              title="Error getting customer applications"
              message={errorResolver(customerApplications.error)}
            />
          </div>
        ) : customerApplications.data &&
          customerApplications.data?.length > 0 ? (
          customerApplications.data?.map((appReg, i) => (
            <div className="py-4 flex justify-between" key={i}>
              <span className="w-28 text-sm font-medium">
                {appReg.application.name}
              </span>
              {appReg.application.system ? (
                <span className="italic text-xs text-gray-600">System app</span>
              ) : (
                <>
                  <MissingApplicationSettingsBadge
                    count={appReg.applicationSettingsMissingValuesCount}
                  />
                  <div className="flex flex-row items-center space-x-4">
                    <LinkIcon
                      className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                      onClick={() =>
                        handleOpenConnString(
                          appReg.applicationSettingsConnectionString
                        )
                      }
                    />
                    <WrenchIcon
                      className="w-5 h-5 opacity-70 hover:opacity-100 hover:cursor-pointer"
                      onClick={() => handleOpenAppSettings(appReg.id)}
                    />
                  </div>
                </>
              )}
              <span className="w-80 flex justify-between items-center text-sm text-gray-800">
                {appReg.id}
                <CopyToClipboardButton copyText={appReg.id} />
              </span>
            </div>
          ))
        ) : (
          <div className="py-10 flex justify-center">
            <span className="text-sm font-medium text-gray-400">
              No applications
            </span>
          </div>
        )}
      </Card>
    </>
  );
}
